import React from 'react';
import Icon from './icons';
import { GENDOCS, getVers } from './gendocs';
import { openS3, saveS3ToProject } from './docgen';
import type { Project } from './types';

function fmtVerTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p2 = (x: number) => (x < 10 ? '0' + x : '' + x);
  return d.getFullYear() + '. ' + (d.getMonth() + 1) + '. ' + d.getDate() + '. ' + p2(d.getHours()) + ':' + p2(d.getMinutes());
}

export function VerModal({ p, k, onPv, onClose, toast }: {
  p: Project; k: string; onPv: (k: string, idx: number) => void; onClose: () => void; toast: (m: string) => void;
}) {
  const vers = getVers(p, k);
  const title = (GENDOCS[k] && GENDOCS[k].title) || k;
  return (
    <div className="ovm">
      <div className="ovbox">
        <div className="ovh"><b>🕘 {title} · 버전 이력</b><button className="povx" onClick={onClose}><Icon n="x" /> 닫기</button></div>
        <div className="verlist">
          {vers.map((v, i) => (
            <div className={'verrow ' + (i === vers.length - 1 ? 'cur' : '')} key={i}>
              <div className="verl"><b>{v.label}</b>{v.kind === 'pdfonly' ? <span className="pill p-amber">PDF만</span> : v.kind === 'seal' ? <span className="pill p-blue">✍ 서명완료</span> : null}{i === vers.length - 1 ? <span className="pill p-green">현재</span> : null}{v.at ? <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>· 생성 {fmtVerTime(v.at)}</span> : null}</div>
              <div className="verf"><span className="rf">📄 {v.doc}</span><button className="rbtn" onClick={() => { if (v.s3doc) void saveS3ToProject(p, v.s3doc, v.doc, toast); else toast('실파일이 없는 구버전입니다 — 재생성 후 이용하세요'); }}>⬇</button></div>
              <div className="verf"><span className="rf">📕 {v.pdf || '-'}</span><button className="rbtn" onClick={() => onPv(k, i)}>👁</button><button className="rbtn" onClick={() => { if (v.s3pdf) void saveS3ToProject(p, v.s3pdf, v.pdf || v.doc, toast); else toast('이 버전에는 PDF가 없습니다'); }}>⬇</button></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** 그림 파일(신분증 사본 등) 인쇄 — 배열(1×1 ~ 4×4) · 방향 · 채우기를 고르고 미리 본 뒤 인쇄 (2026-10-25 지도사)
 *  미리보기와 인쇄가 같은 HTML(sheetHtml)을 써서 보이는 그대로 나온다. PDF는 미리보기 틀에 인쇄 단추가 있어 그림만. */
export interface PrnOpt { c: number; r: number; land: boolean; fill: boolean }
const LAYS: { c: number; r: number }[] = [{ c: 1, r: 1 }, { c: 1, r: 2 }, { c: 2, r: 2 }, { c: 2, r: 3 }, { c: 3, r: 3 }, { c: 4, r: 4 }];
const PRN_KEY = 'venture.prnOpt';
const MM = 96 / 25.4;
function loadOpt(): PrnOpt {
  try {
    const o = JSON.parse(localStorage.getItem(PRN_KEY) || 'null') as PrnOpt | null;
    if (o && LAYS.some((l) => l.c === o.c && l.r === o.r)) return { c: o.c, r: o.r, land: !!o.land, fill: o.fill !== false };
  } catch { /* 저장소 막힘 */ }
  return { c: 1, r: 1, land: false, fill: true };
}
function saveOpt(o: PrnOpt): void { try { localStorage.setItem(PRN_KEY, JSON.stringify(o)); } catch { /* 저장소 막힘 */ } }
const escH = (t: string) => t.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
function sheetDim(o: PrnOpt): { w: number; h: number } { return o.land ? { w: 297, h: 210 } : { w: 210, h: 297 }; }
export function sheetHtml(url: string, name: string, o: PrnOpt): string {
  const { w, h } = sheetDim(o);
  const n = o.c * o.r;
  const cells = Array.from({ length: n }, (_, i) => '<div class="c">' + (o.fill || i === 0 ? '<img alt="" src="' + escH(url) + '">' : '') + '</div>').join('');
  return '<!doctype html><html><head><meta charset="utf-8"><title>' + escH(name) + '</title><style>'
    + '@page{size:A4 ' + (o.land ? 'landscape' : 'portrait') + ';margin:0}html,body{margin:0;padding:0;background:#fff}'
    + '.s{box-sizing:border-box;width:' + w + 'mm;height:' + (h - 1) + 'mm;padding:10mm;overflow:hidden;display:grid;'
    + 'grid-template-columns:repeat(' + o.c + ',minmax(0,1fr));grid-template-rows:repeat(' + o.r + ',minmax(0,1fr));gap:' + (n > 1 ? '5mm' : '0') + '}'
    + '.c{min-width:0;min-height:0;position:relative}'
    + '.c img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;object-position:' + (n === 1 ? 'center top' : 'center') + ';image-orientation:from-image}'
    + '</style></head><body><div class="s">' + cells + '</div></body></html>';
}
function printHtml(html: string, onDone: (ok: boolean) => void): void {
  const fr = document.createElement('iframe');
  fr.setAttribute('aria-hidden', 'true');
  fr.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  fr.srcdoc = html;
  let gone = false;
  const cleanup = () => { if (gone) return; gone = true; window.setTimeout(() => fr.remove(), 500); };
  fr.onload = () => {
    const w = fr.contentWindow;
    const imgs = w ? Array.from(w.document.querySelectorAll('img')) : [];
    if (!w || !imgs.length) { onDone(false); cleanup(); return; }
    const ready = imgs.map((img) => new Promise<boolean>((res) => {
      if (img.complete) { res(!!img.naturalWidth); return; }   // 이미 못 불러온 그림(주소 만료 등)은 false
      img.addEventListener('load', () => res(true), { once: true });
      img.addEventListener('error', () => res(false), { once: true });
    }));
    Promise.all(ready).then((oks) => {
      if (oks.some((x) => !x)) { onDone(false); cleanup(); return; }
      w.addEventListener('afterprint', cleanup);
      window.setTimeout(cleanup, 120000);   // afterprint 가 안 오는 브라우저 대비
      try { w.focus(); w.print(); onDone(true); } catch { onDone(false); cleanup(); }
    }).catch(() => { onDone(false); cleanup(); });
  };
  document.body.appendChild(fr);
}

function PrintSetup({ url, name, onBack }: { url: string; name: string; onBack: () => void }) {
  const [o, setO] = React.useState<PrnOpt>(loadOpt);
  const [st, setSt] = React.useState<'' | 'busy' | 'fail'>('');
  const boxRef = React.useRef<HTMLDivElement>(null);
  const [box, setBox] = React.useState({ w: 600, h: 600 });
  React.useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const fit = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);
  const set = (patch: Partial<PrnOpt>) => { const n = { ...o, ...patch }; setO(n); saveOpt(n); setSt(''); };
  const html = sheetHtml(url, name, o);
  const { w, h } = sheetDim(o);
  const pw = w * MM, ph = h * MM;
  const sc = Math.max(0.1, Math.min((box.w - 40) / pw, (box.h - 40) / ph));
  const n = o.c * o.r;
  const go = () => {
    if (st === 'busy') return;
    setSt('busy');
    printHtml(html, (ok) => setSt(ok ? '' : 'fail'));
  };
  return (
    <div className="prn">
      <div className="prn-pv" ref={boxRef}>
        <div className="prn-sheet" style={{ width: pw * sc, height: ph * sc }}>
          <iframe title="인쇄 미리보기" srcDoc={html} tabIndex={-1} style={{ width: pw, height: ph, transform: 'scale(' + sc + ')' }} />
        </div>
      </div>
      <div className="prn-opt">
        <div className="prn-lbl">배열</div>
        <div className="prn-lays">
          {LAYS.map((l) => {
            const on = l.c === o.c && l.r === o.r;
            return (
              <button type="button" key={l.c + 'x' + l.r} className={'prn-lay' + (on ? ' on' : '')} aria-pressed={on} onClick={() => set({ c: l.c, r: l.r })}>
                <span className="prn-ico" style={{ gridTemplateColumns: 'repeat(' + l.c + ',1fr)', gridTemplateRows: 'repeat(' + l.r + ',1fr)' }}>
                  {Array.from({ length: l.c * l.r }, (_, i) => <i key={i} />)}
                </span>
                <span>{l.c} × {l.r}</span>
              </button>
            );
          })}
        </div>
        <div className="prn-lbl">용지 방향</div>
        <div className="prn-seg">
          <button type="button" className={!o.land ? 'on' : ''} aria-pressed={!o.land} onClick={() => set({ land: false })}>세로</button>
          <button type="button" className={o.land ? 'on' : ''} aria-pressed={o.land} onClick={() => set({ land: true })}>가로</button>
        </div>
        {n > 1 ? (<>
          <div className="prn-lbl">채우기</div>
          <div className="prn-seg">
            <button type="button" className={o.fill ? 'on' : ''} aria-pressed={o.fill} onClick={() => set({ fill: true })}>모든 칸 ({n}장)</button>
            <button type="button" className={!o.fill ? 'on' : ''} aria-pressed={!o.fill} onClick={() => set({ fill: false })}>첫 칸만</button>
          </div>
        </>) : null}
        <div className="prn-note">A4 용지, 여백 10mm<br />인쇄창에서 배율은 「기본」 또는 100%로 두세요</div>
        <span className="grow" />
        {st === 'fail' ? <div className="prn-err">인쇄를 열지 못했어요. 미리보기를 닫고 다시 열어 주세요</div> : null}
        <button type="button" className="btn pri prn-go" disabled={st === 'busy'} onClick={go}>🖨 {st === 'busy' ? '여는 중' : '인쇄'}</button>
        <button type="button" className="btn prn-back" onClick={onBack}>미리보기로 돌아가기</button>
      </div>
    </div>
  );
}

export function PvModal({ name, title, url, onClose }: { name: string; title?: string; url?: string; onClose: () => void }) {
  const isImg = /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name || '');
  const [prnMode, setPrnMode] = React.useState(false);
  const canPrint = !!url && isImg;
  return (
    <div className="ovm">
      <div className="ovbox pvbox" style={url ? { maxWidth: 1280, width: '96%' } : undefined}>
        <div className="ovh"><b>{prnMode ? '🖨 ' + (title || name) + ' 인쇄' : '👁 ' + (title || name) + ' 미리보기'}</b>
          <span className="ovh-acts">
            {canPrint && !prnMode ? <button type="button" className="povx" onClick={() => setPrnMode(true)}>🖨 인쇄</button> : null}
            <button className="povx" onClick={onClose}><Icon n="x" /> 닫기</button>
          </span>
        </div>
        {url && canPrint && prnMode ? (
          <PrintSetup url={url} name={title || name} onBack={() => setPrnMode(false)} />
        ) : url ? (
          isImg ? (
            <div style={{ width: '100%', height: '86vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', background: '#525659', borderRadius: 10 }}>
              <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </div>
          ) : (
            <iframe title={name} src={url} style={{ width: '100%', height: '86vh', border: '1px solid var(--line, #e5e9f2)', borderRadius: 10, background: '#525659' }} />
          )
        ) : (
          <div className="pvpage">
            <div className="pvdoc">📕<div className="docfn">{name}</div><div className="dochint">아직 실파일이 없습니다 · ⬇ Word/PDF 버튼을 누르면 실제 문서가 생성됩니다</div></div>
          </div>
        )}
      </div>
    </div>
  );
}
